/**
 * keyboard.ts — roving-focus helpers for tab/radio strips (WCAG 2.1.1, APG patterns).
 */

/**
 * Move focus to the next/previous [role="tab"|"radio"] sibling within `container`,
 * wrapping around. Returns the newly focused element (or null).
 */
export function focusRovingSibling(
  container: HTMLElement | null,
  current: HTMLElement,
  dir: 1 | -1 | 'first' | 'last',
): HTMLElement | null {
  if (!container) return null;
  const items = Array.from(
    container.querySelectorAll<HTMLElement>('[role="tab"],[role="radio"]'),
  ).filter((el) => !el.hasAttribute('disabled'));
  if (items.length === 0) return null;

  let nextIndex: number;
  if (dir === 'first') {
    nextIndex = 0;
  } else if (dir === 'last') {
    nextIndex = items.length - 1;
  } else {
    const i = items.indexOf(current);
    if (i < 0) return null;
    nextIndex = (i + dir + items.length) % items.length;
  }
  const next = items[nextIndex];
  next?.focus();
  return next ?? null;
}

/**
 * Map an arrow/Home/End keydown to a roving direction, or null if the key is
 * not a navigation key (caller should let it through).
 */
export function arrowDirection(key: string): 1 | -1 | 'first' | 'last' | null {
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return 1;
    case 'ArrowLeft':
    case 'ArrowUp':
      return -1;
    case 'Home':
      return 'first';
    case 'End':
      return 'last';
    default:
      return null;
  }
}
